import { useState } from 'react';
import {
  Box, Typography, Chip, Tooltip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ExpandLess, ExpandMore, RocketLaunch, Star } from '@mui/icons-material';
import { bucketMeta, getTier, getStyle, scoreColor } from './constants';
import { ComponentLabel, RiskBar } from './primitives';
import StyleChip from './StyleChip';

const ActionTable = ({ bucket, items, onClickTicker, sn, tFn }) => {
  const meta = bucketMeta[bucket];
  const count = items?.length || 0;
  const [expanded, setExpanded] = useState(false);
  const DEFAULT_ROWS = 6;
  const visible = expanded ? items : (items || []).slice(0, DEFAULT_ROWS);

  const thS = {
    fontSize: '0.6rem', fontWeight: 700, color: '#64748b',
    letterSpacing: '0.04em', textTransform: 'uppercase', py: 0.6, px: 1,
    borderBottom: `2px solid ${alpha(meta.color, 0.25)}`, bgcolor: alpha(meta.color, 0.04),
    whiteSpace: 'nowrap', lineHeight: 1.3,
  };
  const tdS = {
    py: 0.55, px: 1, fontSize: '0.75rem',
    borderBottom: '1px solid #f1f5f9',
  };

  return (
    <Box sx={{
      bgcolor: '#fff', borderRadius: 2,
      border: `2px solid ${meta.border}`,
      boxShadow: `0 4px 12px ${alpha(meta.color, 0.08)}`,
      overflow: 'hidden', mb: 1.5,
    }}>
      {/* Header band */}
      <Box sx={{
        px: 1.5, py: 1, bgcolor: meta.bg, borderBottom: `1px solid ${meta.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {meta.Icon && <meta.Icon sx={{ fontSize: 18, color: meta.color }} />}
          <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: meta.color }}>
            {tFn('stockStrength.buckets.' + bucket + '.title', meta.title)}
          </Typography>
          <Chip label={tFn('stockStrength.chip.count', { count })} size="small" sx={{
            height: 22, fontSize: '0.7rem', fontWeight: 800,
            bgcolor: meta.color, color: '#fff',
            '& .MuiChip-label': { px: 0.9 },
          }} />
          <Typography sx={{ fontSize: '0.68rem', color: alpha(meta.color, 0.9), fontWeight: 700, fontStyle: 'italic' }}>
            → {tFn('stockStrength.buckets.' + bucket + '.cta', meta.cta)}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.65rem', color: alpha(meta.color, 0.85), fontWeight: 600 }}>
          {tFn('stockStrength.buckets.' + bucket + '.sub', meta.sub)}
        </Typography>
      </Box>

      {count === 0 ? (
        <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', py: 2.5 }}>
          {tFn('stockStrength.actionTable.noSignals')}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" stickyHeader={expanded}>
            <TableHead>
              <TableRow>
                <TableCell sx={thS}>#</TableCell>
                <TableCell sx={thS}>{tFn('stockStrength.table.ticker')}</TableCell>
                <TableCell sx={thS} align="center">{tFn('stockStrength.table.score')}</TableCell>
                <TableCell sx={thS} align="center">{tFn('stockStrength.table.style')}</TableCell>
                <TableCell sx={thS} align="center">{tFn('stockStrength.table.tier')}</TableCell>
                <TableCell sx={thS} align="center">{tFn('stockStrength.table.rs')}</TableCell>
                <TableCell sx={thS} align="center">{tFn('stockStrength.table.trend')}</TableCell>
                <TableCell sx={thS} align="center">{tFn('stockStrength.table.mom')}</TableCell>
                <TableCell sx={thS} align="center">{tFn('stockStrength.table.flow')}</TableCell>
                <TableCell sx={thS} align="center">{tFn('stockStrength.table.resil')}</TableCell>
                <TableCell sx={thS} align="center">{tFn('stockStrength.table.risk')}</TableCell>
                <TableCell sx={{ ...thS, minWidth: 170 }}>{tFn('stockStrength.table.sector')}</TableCell>
                <TableCell sx={{ ...thS, minWidth: 160 }}>{tFn('stockStrength.actionTable.whyAssigned')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((s, i) => {
                const tier = getTier(s.strength_tier);
                const c = s.components || {};
                const lk = s.level_keys || {};
                const auditShort = s.audit_reason_short || (s.audit_reason || '').trim();
                const audit = (s.audit_reason || '').trim();
                return (
                  <TableRow
                    key={s.ticker}
                    hover
                    onClick={() => onClickTicker(s.ticker)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: i % 2 === 0 ? '#fff' : '#fafbfc',
                      '&:hover': { bgcolor: alpha(meta.color, 0.06) },
                      transition: 'background 0.15s ease',
                    }}
                  >
                    <TableCell sx={tdS}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600 }}>{i + 1}</Typography>
                    </TableCell>
                    <TableCell sx={tdS}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <Typography sx={{
                          fontSize: '0.82rem', fontWeight: 800, fontFamily: 'monospace',
                          color: tier.color, letterSpacing: '0.04em',
                        }}>
                          {s.ticker}
                        </Typography>
                        {s.breakout_bar_flag && (
                          <Tooltip arrow title={tFn('stockStrength.badges.breakoutInline')}>
                            <RocketLaunch sx={{ fontSize: 12, color: '#f59e0b' }} />
                          </Tooltip>
                        )}
                        {s.persistent_leader && (
                          <Tooltip arrow title={tFn('stockStrength.badges.persistentLeaderInline')}>
                            <Star sx={{ fontSize: 12, color: '#8b5cf6' }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={tdS} align="center">
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: scoreColor(lk.score) }}>
                        {s.strength_score != null ? Math.round(s.strength_score) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdS} align="center">
                      <StyleChip style={s.trade_style} />
                    </TableCell>
                    <TableCell sx={tdS} align="center">
                      {s.strength_tier && s.strength_tier !== 'REST' ? (
                        <Chip label={`${tier.icon} ${tFn('stockStrength.tiers.' + tier.labelKey)}`} size="small" sx={{
                          height: 18, fontSize: '0.58rem', fontWeight: 700,
                          bgcolor: alpha(tier.color, 0.1), color: tier.color,
                          border: `1px solid ${alpha(tier.color, 0.25)}`,
                          '& .MuiChip-label': { px: 0.6 },
                        }} />
                      ) : (
                        <Typography sx={{ fontSize: '0.6rem', color: '#cbd5e1', fontWeight: 600 }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={tdS} align="center"><ComponentLabel levelKey={lk.rs}    value={c.relative_strength} tFn={tFn} /></TableCell>
                    <TableCell sx={tdS} align="center"><ComponentLabel levelKey={lk.trend} value={c.trend_quality} tFn={tFn} /></TableCell>
                    <TableCell sx={tdS} align="center"><ComponentLabel levelKey={lk.mom}   value={c.momentum_quality} tFn={tFn} /></TableCell>
                    <TableCell sx={tdS} align="center"><ComponentLabel levelKey={lk.flow}  value={c.money_flow} tFn={tFn} /></TableCell>
                    <TableCell sx={tdS} align="center"><ComponentLabel levelKey={lk.resil} value={c.resilience} tFn={tFn} /></TableCell>
                    <TableCell sx={tdS} align="center"><RiskBar levelKey={lk.risk} value={c.risk_penalty} t={tFn} /></TableCell>
                    <TableCell sx={tdS}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {sn(s.sector)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdS}>
                      {audit ? (
                        <Tooltip arrow placement="left" title={
                          <Box sx={{ p: 0.5, maxWidth: 340 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
                              {getStyle(s.trade_style).Icon && (() => {
                                const SIcon = getStyle(s.trade_style).Icon;
                                return <SIcon sx={{ fontSize: 14 }} />;
                              })()}
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>
                                {getStyle(s.trade_style).label}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.7rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                              {audit}
                            </Typography>
                          </Box>
                        }>
                          <Typography sx={{
                            fontSize: '0.65rem', color: '#475569', lineHeight: 1.35,
                            cursor: 'help',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {auditShort}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: '0.6rem', color: '#cbd5e1' }}>—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {count > DEFAULT_ROWS && (
        <Box sx={{
          px: 1.5, py: 0.7, borderTop: '1px dashed #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafbfc',
        }}>
          <Button
            size="small"
            onClick={() => setExpanded(v => !v)}
            startIcon={expanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none', fontSize: '0.7rem', fontWeight: 700,
              color: meta.color, '&:hover': { bgcolor: alpha(meta.color, 0.08) },
            }}
          >
            {expanded
              ? tFn('stockStrength.expand.collapse', { count: DEFAULT_ROWS, total: count })
              : tFn('stockStrength.expand.viewMore', { remaining: count - DEFAULT_ROWS, total: count })}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ActionTable;
